import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { LoginForm } from '@/components/auth/LoginForm';
import { AuthLogo } from '@/components/auth/AuthLogo';
import { useToast } from "@/components/ui/use-toast";

const Login: FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log('Checking session status...');
        
        // Clear any existing session data to prevent token conflicts
        localStorage.removeItem('supabase.auth.token');
        await supabase.auth.signOut();
        
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check error:', error);
          setIsCheckingSession(false);
          return;
        }

        if (session) {
          console.log('Active session found, redirecting to dashboard');
          // Check user role to determine redirect
          const { data: profile } = await supabase
            .from('profiles')
            .select('role')
            .eq('id', session.user.id)
            .single();
            
          const redirectPath = profile?.role === 'admin' ? '/admin' : '/dashboard';
          navigate(redirectPath, { replace: true });
        } else {
          console.log('No active session found');
          setIsCheckingSession(false);
        }
      } catch (error) {
        console.error('Session check failed:', error);
        setIsCheckingSession(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session ? 'authenticated' : 'unauthenticated');
      if (session) {
        // Check user role to determine redirect
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .single();
          
        const redirectPath = profile?.role === 'admin' ? '/admin' : '/dashboard';
        navigate(redirectPath, { replace: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f6fa] p-3 md:p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f6fa] p-3 md:p-6">
      <div className="w-full max-w-6xl flex flex-col md:flex-row md:gap-6 items-start">
        {/* Columna izquierda - Copy */}
        <div className="w-full md:w-1/2 space-y-5">
          <div className="flex flex-col items-center space-y-4 md:items-start">
            <div className="w-full flex justify-center md:justify-start">
              <AuthLogo className="w-auto h-20" />
            </div>
            <div className="text-center md:text-left space-y-2">
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                ¡Qué bueno verte de nuevo!
              </h1>
              <p className="text-lg text-gray-600">
                Continúa tu viaje de aprendizaje financiero
              </p>
            </div>
          </div>

          <div className="bg-primary/5 rounded-lg p-5 space-y-3">
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="text-2xl">💰</span> Aprende mientras juegas
              </h3>
              <p className="text-gray-600">
                Descubre el mundo de las finanzas a través de juegos y actividades interactivas
                diseñadas especialmente para niños de 8 a 12 años.
              </p>
            </div>
          </div>
        </div>

        {/* Columna derecha - Formulario */}
        <div className="w-full md:w-1/2 md:mt-0 mt-5">
          <Card className="w-full shadow-sm">
            <CardHeader className="space-y-1 text-center md:text-left pb-4">
              <CardTitle className="text-2xl font-bold">Iniciar sesión</CardTitle>
              <CardDescription>
                Ingresa tus datos para continuar
              </CardDescription>
            </CardHeader>

            <CardContent>
              <LoginForm 
                isLoading={isLoading} 
                setIsLoading={setIsLoading} 
                onSubmit={async (email, password) => {
                  setIsLoading(true);

                  try {
                    const { data, error } = await supabase.auth.signInWithPassword({
                      email,
                      password,
                    });

                    if (error) {
                      // Verificar si el error es por email no confirmado
                      if (error.message.includes('Email not confirmed')) {
                        toast({
                          title: "Correo no verificado",
                          description: (
                            <div className="space-y-2">
                              <p>Tu correo electrónico aún no ha sido verificado.</p>
                              <p>👉 Por favor, revisa tu bandeja de entrada y haz clic en el enlace de verificación que te enviamos.</p>
                              <div className="mt-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      const { error: resendError } = await supabase.auth.resend({
                                        type: 'signup',
                                        email,
                                      });
                                      if (resendError) throw resendError;
                                      toast({
                                        title: "Correo reenviado",
                                        description: "Hemos enviado un nuevo correo de verificación.",
                                      });
                                    } catch (err: any) {
                                      toast({
                                        title: "Error",
                                        description: err.message,
                                        variant: "destructive",
                                      });
                                    }
                                  }}
                                >
                                  Reenviar correo de verificación
                                </Button>
                              </div>
                            </div>
                          ),
                          variant: "destructive",
                          duration: 10000, // 10 segundos para dar tiempo a leer
                        });
                      } else {
                        toast({
                          title: "Error",
                          description: error.message,
                          variant: "destructive",
                        });
                      }
                      return;
                    }

                    if (data?.user) {
                      navigate("/dashboard");
                    }
                  } catch (error: any) {
                    toast({
                      title: "Error",
                      description: error.message,
                      variant: "destructive",
                    });
                  } finally {
                    setIsLoading(false);
                  }
                }} 
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;